import { useState, useCallback } from 'react';
import { quizApi, QuizSessionResponse } from '../services/quizApi';

// Interfaccia estesa per il frontend
export interface QuizSession extends QuizSessionResponse {
  // Campi aggiunti dal frontend per gestione locale
  currentQuestionIndex?: number;
  answers?: QuizAnswer[];
}

export interface Question {
  id: string;
  text: string;
  difficulty: number;
  explanation?: string;
  answerOptions: AnswerOption[];
}

export interface AnswerOption {
  id: string;
  text: string;
  isCorrect: boolean;
  order: number;
}

export interface QuizAnswer {
  questionId: string;
  selectedAnswerId: string;
  timeSpent: number;
}

interface QuizState {
  session: QuizSession | null;
  currentQuestionIndex: number;
  answers: Map<string, QuizAnswer>;
  timeSpent: number;
  isSubmitting: boolean;
  error: string | null;
}

export const useQuiz = () => {
  const [state, setState] = useState<QuizState>({
    session: null,
    currentQuestionIndex: 0,
    answers: new Map(),
    timeSpent: 0,
    isSubmitting: false,
    error: null
  });

  // Avvia nuovo quiz creando sessione nel backend
  const startQuiz = useCallback(async (subjectId: string, questionCount: number) => {
    try {
      setState(prev => ({ ...prev, isSubmitting: true, error: null }));
      
      // Crea sessione nel backend
      const backendSession: QuizSessionResponse = await quizApi.createSession(subjectId, questionCount);
      
      // Converti la sessione del backend in una sessione del frontend
      const frontendSession: QuizSession = {
        ...backendSession,
        currentQuestionIndex: 0,
        answers: []
      };
      
      setState(prev => ({
        ...prev,
        session: frontendSession,
        currentQuestionIndex: 0,
        answers: new Map(),
        timeSpent: 0,
        isSubmitting: false
      }));
      
      return frontendSession;
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message || 'Errore durante la creazione del quiz',
        isSubmitting: false
      }));
      throw error;
    }
  }, []);

  // Carica sessione esistente
  const loadSession = useCallback(async (sessionId: string) => {
    try {
      setState(prev => ({ ...prev, isSubmitting: true, error: null }));
      
      const backendSession: QuizSessionResponse = await quizApi.getSession(sessionId);
      
      // Converti la sessione del backend in una sessione del frontend
      setState(prev => {
        const frontendSession: QuizSession = {
          ...backendSession,
          currentQuestionIndex: prev.currentQuestionIndex, // Mantieni l'indice locale
          answers: prev.answers.size > 0 ? Array.from(prev.answers.values()) : [] // Mantieni le risposte locali
        };
        
        return {
          ...prev,
          session: frontendSession,
          isSubmitting: false
        };
      });
      
      return state.session;
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message || 'Errore durante il caricamento della sessione',
        isSubmitting: false
      }));
      throw error;
    }
  }, [state.session]); // Solo session è necessario

  // Salva risposta localmente (senza invio al server)
  const submitAnswer = useCallback((questionId: string, answerId: string, timeSpent: number) => {
    if (!state.session) return;

    const answer: QuizAnswer = {
      questionId,
      selectedAnswerId: answerId,
      timeSpent
    };

    // Salva risposta localmente
    const newAnswers = new Map(state.answers);
    newAnswers.set(questionId, answer);
    
    console.log('💾 [DEBUG] Risposta salvata localmente:', {
      questionId,
      answerId,
      timeSpent,
      totalAnswers: newAnswers.size
    });
    
    setState(prev => ({
      ...prev,
      answers: newAnswers,
      timeSpent: prev.timeSpent + timeSpent
    }));
  }, [state.session, state.answers]);

  // Prossima domanda
  const nextQuestion = useCallback(() => {
    if (!state.session) return;
    
    setState(prev => ({
      ...prev,
      currentQuestionIndex: Math.min(prev.currentQuestionIndex + 1, prev.session!.totalQuestions - 1)
    }));
  }, [state.session]);

  // Domanda precedente
  const previousQuestion = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentQuestionIndex: Math.max(prev.currentQuestionIndex - 1, 0)
    }));
  }, []);

  // Completa quiz locale e invia risultati al backend
  const finishQuiz = useCallback(async () => {
    if (!state.session) return;

    try {
      setState(prev => ({ ...prev, isSubmitting: true }));
      
      // Invia tutti i risultati al backend
      const allAnswers = Array.from(state.answers.values());
      
      console.log('📊 [DEBUG] Inviando risposte al backend:', {
        sessionId: state.session.id,
        totalAnswers: allAnswers.length,
        answers: allAnswers.map(a => ({ questionId: a.questionId, answerId: a.selectedAnswerId }))
      });
      
      const completedSession = await quizApi.submitQuizResults(
        state.session.id, 
        allAnswers, 
        state.timeSpent
      );
      
      console.log('✅ [DEBUG] Risposta dal backend ricevuta:', {
        sessionId: completedSession.id,
        status: completedSession.status
      });
      
      setState(prev => ({
        ...prev,
        session: completedSession,
        isSubmitting: false
      }));
      
      return completedSession;
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message || 'Errore durante il completamento del quiz',
        isSubmitting: false
      }));
      throw error;
    }
  }, [state.session, state.answers, state.timeSpent]);

  // Completa quiz con risposte specifiche (per evitare problemi di stato)
  const finishQuizWithAnswers = useCallback(async (answers: QuizAnswer[]) => {
    if (!state.session) return;

    try {
      setState(prev => ({ ...prev, isSubmitting: true }));
      
      console.log('📊 [DEBUG] Inviando risposte specifiche al backend:', {
        sessionId: state.session.id,
        sessionStatus: state.session.status,
        totalAnswers: answers.length,
        answers: answers.map(a => ({ questionId: a.questionId, answerId: a.selectedAnswerId }))
      });
      
      // Controlla se la sessione è già completata
      if (state.session.status === 'completed') {
        console.log('⚠️ [WARNING] Sessione già completata, non invio risultati');
        return state.session;
      }
      
      const completedSession = await quizApi.submitQuizResults(
        state.session.id, 
        answers, 
        state.timeSpent
      );
      
      console.log('✅ [DEBUG] Risposta dal backend ricevuta:', {
        sessionId: completedSession.id,
        status: completedSession.status
      });
      
      setState(prev => ({
        ...prev,
        session: completedSession,
        isSubmitting: false
      }));
      
      return completedSession;
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message || 'Errore durante il completamento del quiz',
        isSubmitting: false
      }));
      throw error;
    }
  }, [state.session, state.timeSpent]);

  // Metti in pausa quiz
  const pauseQuiz = useCallback(() => {
    if (!state.session) return;
    
    setState(prev => ({
      ...prev,
      session: prev.session ? { 
        ...prev.session, 
        status: 'paused'
      } : null
    }));
  }, [state.session]);

  // Riprendi quiz
  const resumeQuiz = useCallback(() => {
    if (!state.session) return;
    
    setState(prev => ({
      ...prev,
      session: prev.session ? { 
        ...prev.session, 
        status: 'active'
      } : null
    }));
  }, [state.session]);

  // Reset quiz
  // Reset dello stato locale (mantiene la sessione)
  const resetLocalState = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentQuestionIndex: 0,
      answers: new Map(),
      timeSpent: 0
    }));
  }, []);

  const resetQuiz = useCallback(() => {
    setState({
      session: null,
      currentQuestionIndex: 0,
      answers: new Map(),
      timeSpent: 0,
      isSubmitting: false,
      error: null
    });
  }, []);

  // Ottieni risposta per domanda
  const getAnswerForQuestion = useCallback((questionId: string): QuizAnswer | undefined => {
    return state.answers.get(questionId);
  }, [state.answers]);

  // Ottieni risposta selezionata per domanda
  const getSelectedAnswerForQuestion = useCallback((questionId: string): string | null => {
    const answer = state.answers.get(questionId);
    return answer?.selectedAnswerId || null;
  }, [state.answers]);

  // Controlla se domanda è stata risposta
  const isQuestionAnswered = useCallback((questionId: string): boolean => {
    return state.answers.has(questionId);
  }, [state.answers]);

  // Ottieni tutte le risposte
  const getAllAnswers = useCallback((): QuizAnswer[] => {
    return Array.from(state.answers.values());
  }, [state.answers]);

  // Ottieni domanda corrente
  const getCurrentQuestion = useCallback((): Question | null => {
    if (!state.session || !state.session.questions[state.currentQuestionIndex]) {
      return null;
    }
    return state.session.questions[state.currentQuestionIndex];
  }, [state.session, state.currentQuestionIndex]);

  // Calcola progresso
  const getProgress = useCallback((): number => {
    if (!state.session) return 0;
    return ((state.currentQuestionIndex + 1) / state.session.totalQuestions) * 100;
  }, [state.session, state.currentQuestionIndex]);

  // Controlla se è ultima domanda
  const isLastQuestion = useCallback((): boolean => {
    if (!state.session) return false;
    return state.currentQuestionIndex === state.session.totalQuestions - 1;
  }, [state.session, state.currentQuestionIndex]);

  // Controlla se è prima domanda
  const isFirstQuestion = useCallback((): boolean => {
    return state.currentQuestionIndex === 0;
  }, [state.currentQuestionIndex]);

  // Cancella sessione dal backend
  const cancelSession = useCallback(async () => {
    if (!state.session) return;

    try {
      console.log('🗑️ [DEBUG] Cancellando sessione:', state.session.id);
      await quizApi.deleteSession(state.session.id);
      
      // Reset stato locale
      setState(prev => ({
        ...prev,
        session: null,
        currentQuestionIndex: 0,
        answers: new Map(),
        timeSpent: 0,
        isSubmitting: false,
        error: null
      }));
      
      console.log('✅ [DEBUG] Sessione cancellata con successo');
    } catch (error: any) {
      console.error('❌ [ERROR] Errore durante la cancellazione della sessione:', error);
      setState(prev => ({
        ...prev,
        error: error.message || 'Errore durante la cancellazione della sessione'
      }));
      throw error;
    }
  }, [state.session]);

  return {
    // Stato
    session: state.session,
    currentQuestionIndex: state.currentQuestionIndex,
    answers: state.answers,
    timeSpent: state.timeSpent,
    isSubmitting: state.isSubmitting,
    error: state.error,
    
    // Azioni
    startQuiz,
    loadSession,
    submitAnswer,
    nextQuestion,
    previousQuestion,
    finishQuiz,
    pauseQuiz,
    resumeQuiz,
    resetQuiz,
    resetLocalState,
    cancelSession,
    
    // Utility
    getAnswerForQuestion,
    getSelectedAnswerForQuestion,
    isQuestionAnswered,
    getCurrentQuestion,
    getProgress,
    isLastQuestion,
    isFirstQuestion,
    finishQuizWithAnswers,
    getAllAnswers
  };
};
